import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Tabs,
  Stack,
  Group,
  Text,
  Title,
  Badge,
  ScrollArea,
  Box,
  ThemeIcon,
  Divider,
  Anchor,
  Button,
} from '@mantine/core';
import { RichTextNotesEditor } from '@/components/RichTextNotesEditor';
import {
  IconList,
  IconInfoCircle,
  IconNotebook,
  IconPlayerPlayFilled,
  IconLock,
  IconClock,
  IconArrowLeft,
  IconShare,
  IconBookmark,
  IconHome,
  IconCheck,
  IconTextCaption,
} from '@tabler/icons-react';
import type { Course, CourseSession, CourseModelView } from '@actschurch/ccms-sdk';
import { RichText } from '@/components/common/RichText';
import { courseHref } from '@/utils/slug';
import { useSessionNotes } from '@/hooks/useSessionNotes';
import { TranscriptPanel, type TranscriptPanelProps } from '@/components/player/TranscriptPanel';

export interface CurriculumRailProps {
  course: Course | CourseModelView;
  sessions: CourseSession[];
  currentSessionId: number;
  /**
   * Returns true if the supplied session row is gated for the current viewer
   * (e.g. anonymous viewer + session status = "Public - Signed In"). Per-row
   * — the rail can show a lock on individual rows while leaving siblings
   * playable.
   */
  isSessionLocked?: (session: CourseSession) => boolean;
  /** Resolved logged-in user id (used to scope notes); undefined for anon. */
  userId?: string | null;
  /** Filename stem used by the notes export (txt/doc/pdf). */
  notesExportName?: string;
  /**
   * Optional transcript for the current session. When set, a "Transcript"
   * tab is added; when null or absent the rail is unchanged.
   */
  transcript?: TranscriptPanelProps | null;
}

/**
 * Right-side rail for the immersive player. Three tabs:
 *  - Up next: every session in the course, current highlighted, lock icon on
 *    gated rows the viewer can't reach.
 *  - Overview: course title + description + ministry/category chips.
 *  - Notes: viewer's personal notes for this session (autosaved to
 *    localStorage every 450ms).
 *  - Transcript (optional): follow along, search, click to seek, download.
 */
export function CurriculumRail({
  course,
  sessions,
  currentSessionId,
  isSessionLocked,
  userId,
  notesExportName,
  transcript,
}: CurriculumRailProps) {
  const [tab, setTab] = useState<string | null>('up-next');
  const courseUrl = courseHref(course);
  const enriched = (course as Partial<CourseModelView>);
  // Fall back to "Up next" if the transcript goes away (e.g. next session has none).
  const activeTab = tab === 'transcript' && !transcript ? 'up-next' : tab;

  return (
    <Stack gap={0} h="100%">
      <Tabs
        value={activeTab}
        onChange={setTab}
        variant="default"
        keepMounted={false}
        h="100%"
        styles={{
          root: { display: 'flex', flexDirection: 'column', height: '100%' },
          // Four tabs share the rail width, so tighten the padding a little.
          ...(transcript ? { tab: { paddingInline: 6 } } : {}),
        }}
      >
        <Tabs.List grow>
          <Tabs.Tab value="up-next" leftSection={<IconList size={14} />}>
            Up next
          </Tabs.Tab>
          <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={14} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="notes" leftSection={<IconNotebook size={14} />}>
            Notes
          </Tabs.Tab>
          {transcript && (
            <Tabs.Tab value="transcript" leftSection={<IconTextCaption size={14} />}>
              Transcript
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="up-next" style={{ flex: 1, minHeight: 0 }}>
          <ScrollArea h="100%" type="auto" offsetScrollbars scrollbarSize={6}>
            <Stack gap={6} p="sm">
              <Group justify="space-between" mb={4}>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                  Curriculum
                </Text>
                <Anchor component={Link} to={courseUrl} size="xs">
                  <Group gap={4}>
                    <IconArrowLeft size={12} />
                    Course
                  </Group>
                </Anchor>
              </Group>
              {sessions.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  No other sessions in this course.
                </Text>
              ) : (
                sessions.map((s, i) => (
                  <SessionRow
                    key={s.sessionID}
                    session={s}
                    index={i}
                    isCurrent={s.sessionID === currentSessionId}
                    locked={isSessionLocked?.(s) ?? false}
                    courseUrl={courseUrl}
                  />
                ))
              )}
            </Stack>
          </ScrollArea>
        </Tabs.Panel>

        <Tabs.Panel value="overview" style={{ flex: 1, minHeight: 0 }}>
          <ScrollArea h="100%" type="auto" offsetScrollbars scrollbarSize={6}>
            <Stack gap="md" p="md">
              <Title order={4}>{course.name ?? 'Course'}</Title>
              <Group gap={6}>
                {enriched.ministryName && (
                  <Badge variant="light" color="brand" size="sm">
                    {enriched.ministryName}
                  </Badge>
                )}
                {enriched.categoryName && (
                  <Badge variant="light" color={enriched.categoryColor ?? 'gray'} size="sm">
                    {enriched.categoryName}
                  </Badge>
                )}
                {enriched.visibilityName && (
                  <Badge variant="light" color={enriched.visibilityColor ?? 'gray'} size="sm">
                    {enriched.visibilityName}
                  </Badge>
                )}
              </Group>
              <Divider />
              <RichText html={course.description} />
              <Divider />
              <Group gap="md">
                <SummaryStat label="Sessions" value={sessions.length} />
                <SummaryStat
                  label="Total"
                  value={formatDuration(sessions.reduce((acc, s) => acc + (s.durationInSeconds ?? 0), 0))}
                />
              </Group>

              <Divider label="Actions" labelPosition="left" />
              <ActionRow />

              <Stack gap="xs">
                <Button component={Link} to={courseUrl} variant="light" fullWidth leftSection={<IconArrowLeft size={14} />}>
                  Course overview
                </Button>
                <Button component={Link} to="/" variant="subtle" fullWidth leftSection={<IconHome size={14} />}>
                  Resource Centre home
                </Button>
              </Stack>
            </Stack>
          </ScrollArea>
        </Tabs.Panel>

        <Tabs.Panel value="notes" style={{ flex: 1, minHeight: 0 }}>
          <NotesPanel sessionId={currentSessionId} userId={userId} exportName={notesExportName} />
        </Tabs.Panel>

        {transcript && (
          <Tabs.Panel value="transcript" style={{ flex: 1, minHeight: 0 }}>
            <TranscriptPanel key={currentSessionId} {...transcript} />
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}

function SessionRow({
  session,
  index,
  isCurrent,
  locked,
  courseUrl,
}: {
  session: CourseSession;
  index: number;
  isCurrent: boolean;
  locked: boolean;
  courseUrl: string;
}) {
  const inner = (
    <Group
      gap="sm"
      wrap="nowrap"
      align="flex-start"
      px="sm"
      py="xs"
      style={{
        borderRadius: 'var(--mantine-radius-md)',
        background: isCurrent ? 'var(--mantine-color-brand-light)' : 'transparent',
        cursor: locked ? 'not-allowed' : 'pointer',
        opacity: locked ? 0.55 : 1,
        transition: 'background 120ms ease',
      }}
    >
      <ThemeIcon
        size={28}
        radius="xl"
        variant={isCurrent ? 'filled' : 'light'}
        color={locked ? 'gray' : 'brand'}
      >
        {locked ? <IconLock size={12} /> : isCurrent ? <IconPlayerPlayFilled size={12} /> : <Text size="xs" fw={700}>{index + 1}</Text>}
      </ThemeIcon>
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={isCurrent ? 600 : 500} lineClamp={2}>
          {session.name ?? `Session ${index + 1}`}
        </Text>
        <Group gap={4}>
          <IconClock size={10} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed">
            {formatDuration(session.durationInSeconds ?? 0)}
          </Text>
        </Group>
      </Stack>
    </Group>
  );

  if (locked) return <Box>{inner}</Box>;
  return (
    <Box
      component={Link}
      to={`${courseUrl}/play/${session.sessionID}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      {inner}
    </Box>
  );
}

function SummaryStat({ label, value }: { label: string; value: string | number }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700}>{value}</Text>
    </Stack>
  );
}

/**
 * Compact row of session-scoped actions for the Overview tab. Each tile is a
 * full button (instead of bare icons) so the labels are explicit on touch
 * devices where tooltips don't fire.
 */
function ActionRow() {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <Group gap="xs" grow>
      <Button
        variant="default"
        size="sm"
        leftSection={copied ? <IconCheck size={14} /> : <IconShare size={14} />}
        onClick={copyLink}
      >
        {copied ? 'Copied' : 'Share'}
      </Button>
      <Button
        variant="default"
        size="sm"
        leftSection={<IconBookmark size={14} />}
        disabled
      >
        Bookmark
      </Button>
    </Group>
  );
}

function NotesPanel({
  sessionId,
  userId,
  exportName,
}: {
  sessionId: number;
  userId?: string | null;
  exportName?: string;
}) {
  const { value, setValue, savedAt, clear } = useSessionNotes(sessionId, userId);
  return (
    <Box p="sm" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <RichTextNotesEditor
        value={value}
        onChange={setValue}
        savedAt={savedAt}
        onClear={clear}
        exportName={exportName}
      />
    </Box>
  );
}

function formatDuration(sec: number): string {
  if (!sec || sec <= 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
